import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Edit2, Save, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import ImageUploadField from "../components/ImageUploadField";

const ENTITIES = {
  releases: {
    label: "Releases",
    fields: [
      { key: "catalogue", label: "Catalogue (e.g. NCCC0001)" },
      { key: "artist", label: "Artist" },
      { key: "title", label: "Title" },
      { key: "format", label: 'Format (e.g. 7" single)' },
      { key: "year", label: "Year" },
      { key: "cover_url", label: "Cover image", upload: true },
      { key: "audio_url", label: "Audio (YouTube / SoundCloud / Bandcamp / mp3)", upload: true },
      { key: "listen_url", label: "Listen URL (external)" },
      { key: "buy_url", label: "Buy URL" },
      { key: "description", label: "Description", textarea: true },
    ],
  },
  portals: {
    label: "Portals",
    fields: [
      { key: "name", label: "Name" },
      { key: "kind", label: "Kind (event, zine, podcast, video, broadcast)" },
      { key: "date", label: "Date (YYYY-MM-DD)" },
      { key: "location", label: "Location" },
      { key: "image_url", label: "Image", upload: true },
      { key: "link_url", label: "Link URL" },
      { key: "description", label: "Description", textarea: true },
    ],
  },
  affiliates: {
    label: "Affiliates",
    fields: [
      { key: "name", label: "Name" },
      { key: "kind", label: "Kind (label, venue, artist, collective, zine)" },
      { key: "region", label: "Region" },
      { key: "image_url", label: "Image", upload: true },
      { key: "link_url", label: "Link URL" },
      { key: "description", label: "Description", textarea: true },
    ],
  },
};

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("releases");

  if (checking) {
    return <p className="label p-12">// authenticating...</p>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-12"
      data-testid="admin-dashboard"
    >
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <span className="label-accent">// Operator console</span>
          <h1 className="broadcast-headline text-4xl lg:text-5xl mt-2 text-[color:var(--nccc-cream)]">
            Transmission control.
          </h1>
          <p className="label mt-2">signed in as {user.email}</p>
        </div>
        <button
          onClick={logout}
          className="nccc-btn nccc-btn-ghost"
          data-testid="admin-logout-btn"
        >
          Sign out
        </button>
      </header>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-[color:var(--nccc-border)]">
        {Object.entries(ENTITIES).map(([key, conf]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`px-5 py-3 label transition-colors ${
              tab === key
                ? "text-[color:var(--nccc-ochre)] border-b-2 border-[color:var(--nccc-ochre)]"
                : "hover:text-[color:var(--nccc-cream)]"
            }`}
          >
            {conf.label}
          </button>
        ))}
        <button
          onClick={() => setTab("submissions")}
          data-testid="admin-tab-submissions"
          className={`px-5 py-3 label transition-colors ${
            tab === "submissions"
              ? "text-[color:var(--nccc-ochre)] border-b-2 border-[color:var(--nccc-ochre)]"
              : "hover:text-[color:var(--nccc-cream)]"
          }`}
        >
          Submissions
        </button>
        <button
          onClick={() => setTab("newsletter")}
          data-testid="admin-tab-newsletter"
          className={`px-5 py-3 label transition-colors ${
            tab === "newsletter"
              ? "text-[color:var(--nccc-ochre)] border-b-2 border-[color:var(--nccc-ochre)]"
              : "hover:text-[color:var(--nccc-cream)]"
          }`}
        >
          Newsletter
        </button>
      </div>

      {tab === "submissions" ? (
        <SubmissionsPanel />
      ) : tab === "newsletter" ? (
        <NewsletterPanel />
      ) : (
        <EntityPanel entityKey={tab} conf={ENTITIES[tab]} />
      )}
    </div>
  );
}

function EntityPanel({ entityKey, conf }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get(`/admin/${entityKey}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [entityKey]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await api.delete(`/admin/${entityKey}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const emptyDraft = () => {
    const o = { published: true };
    conf.fields.forEach((f) => (o[f.key] = ""));
    return o;
  };

  return (
    <div data-testid={`admin-table-${entityKey}`}>
      <div className="flex justify-between items-center mb-4">
        <p className="label">{items.length} entries</p>
        <button
          className="nccc-btn"
          onClick={() => setCreating(true)}
          data-testid={`admin-add-${entityKey}`}
        >
          <Plus size={14} /> New
        </button>
      </div>

      {loading ? (
        <p className="label">// loading...</p>
      ) : (
        <div className="border border-[color:var(--nccc-border)]">
          <table className="w-full text-sm">
            <thead className="border-b border-[color:var(--nccc-border)]">
              <tr className="text-left label">
                <th className="p-3">Identifier</th>
                <th className="p-3">Detail</th>
                <th className="p-3">State</th>
                <th className="p-3 text-right">Ops</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-[color:var(--nccc-border)]/60"
                >
                  <td className="p-3 text-[color:var(--nccc-cream)]">
                    {it.catalogue || it.name}
                  </td>
                  <td className="p-3 text-[color:var(--nccc-muted)]">
                    {it.title || it.kind || it.region}
                  </td>
                  <td className="p-3">
                    <span className="label-accent">
                      {it.published ? "PUBLISHED" : "DRAFT"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      className="text-[color:var(--nccc-muted)] hover:text-[color:var(--nccc-ochre)] mr-3"
                      onClick={() => setEditing(it)}
                      data-testid={`admin-edit-${entityKey}-${it.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="text-[color:var(--nccc-muted)] hover:text-[color:var(--nccc-rust)]"
                      onClick={() => onDelete(it.id)}
                      data-testid={`admin-delete-${entityKey}-${it.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan="4" className="p-6 label text-center">
                    // no entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <EntityForm
          entityKey={entityKey}
          conf={conf}
          initial={editing || emptyDraft()}
          isNew={creating}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function EntityForm({ entityKey, conf, initial, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, published: form.published !== false };
      if (isNew) {
        await api.post(`/admin/${entityKey}`, body);
        toast.success("Created");
      } else {
        await api.put(`/admin/${entityKey}/${form.id}`, body);
        toast.success("Updated");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="admin-entity-form"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(12,12,11,0.85)", backdropFilter: "blur(6px)" }}
        onClick={onClose}
      />
      <form
        onSubmit={onSubmit}
        className="relative z-10 max-w-2xl w-full border border-[color:var(--nccc-border)] bg-[color:var(--nccc-surface)] p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <span className="label-accent">// {isNew ? "New" : "Edit"}</span>
            <h3 className="font-display text-2xl text-[color:var(--nccc-cream)] mt-1">
              {conf.label}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[color:var(--nccc-muted)] hover:text-[color:var(--nccc-cream)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {conf.fields.map((f) =>
            f.upload ? (
              <div key={f.key}>
                <label className="label">{f.label}</label>
                <div className="mt-2">
                  <ImageUploadField
                    value={form[f.key] || ""}
                    onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
                    testid={`admin-field-${entityKey}-${f.key}`}
                  />
                </div>
              </div>
            ) : f.textarea ? (
              <div key={f.key}>
                <label className="label">{f.label}</label>
                <textarea
                  className="nccc-input mt-2 min-h-[100px]"
                  value={form[f.key] || ""}
                  onChange={onChange(f.key)}
                  data-testid={`admin-field-${entityKey}-${f.key}`}
                />
              </div>
            ) : (
              <div key={f.key}>
                <label className="label">{f.label}</label>
                <input
                  className="nccc-input mt-2"
                  value={form[f.key] || ""}
                  onChange={onChange(f.key)}
                  data-testid={`admin-field-${entityKey}-${f.key}`}
                />
              </div>
            )
          )}
          <label className="flex items-center gap-3 label cursor-pointer">
            <input
              type="checkbox"
              checked={form.published !== false}
              onChange={(e) =>
                setForm((s) => ({ ...s, published: e.target.checked }))
              }
              data-testid={`admin-field-${entityKey}-published`}
            />
            Published
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button
            type="button"
            onClick={onClose}
            className="nccc-btn nccc-btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="nccc-btn"
            data-testid="admin-form-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NewsletterPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .get("/admin/newsletter")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Remove subscriber?")) return;
    await api.delete(`/admin/newsletter/${id}`);
    load();
  };

  const onExport = () => {
    const csv =
      "email,source,created_at\n" +
      items.map((s) => `${s.email},${s.source || ""},${s.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nccc-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex justify-between items-center mb-4">
        <p className="label">{items.length} subscribers on the signal</p>
        <button
          onClick={onExport}
          className="nccc-btn nccc-btn-ghost"
          disabled={items.length === 0}
          data-testid="admin-newsletter-export"
        >
          Export CSV
        </button>
      </div>
      {loading ? (
        <p className="label">// loading...</p>
      ) : items.length === 0 ? (
        <p className="label">// no subscribers yet.</p>
      ) : (
        <div className="border border-[color:var(--nccc-border)]">
          <table className="w-full text-sm">
            <thead className="border-b border-[color:var(--nccc-border)]">
              <tr className="text-left label">
                <th className="p-3">Email</th>
                <th className="p-3">Source</th>
                <th className="p-3">Joined</th>
                <th className="p-3 text-right">Ops</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-[color:var(--nccc-border)]/60">
                  <td className="p-3 text-[color:var(--nccc-cream)]">{s.email}</td>
                  <td className="p-3 text-[color:var(--nccc-muted)]">{s.source || "—"}</td>
                  <td className="p-3 text-[color:var(--nccc-muted)] text-xs">
                    {new Date(s.created_at).toUTCString().slice(5, 22)}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => onDelete(s.id)}
                      className="text-[color:var(--nccc-muted)] hover:text-[color:var(--nccc-rust)]"
                      data-testid={`admin-newsletter-delete-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .get("/admin/submissions")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete submission?")) return;
    await api.delete(`/admin/submissions/${id}`);
    load();
  };

  return (
    <div data-testid="admin-submissions">
      <p className="label mb-4">{items.length} transmissions received</p>
      {loading ? (
        <p className="label">// loading...</p>
      ) : items.length === 0 ? (
        <p className="label">// nothing in the inbox.</p>
      ) : (
        <div className="space-y-4">
          {items.map((s) => (
            <div
              key={s.id}
              className="border border-[color:var(--nccc-border)] p-5 bg-[color:var(--nccc-surface)]/60"
            >
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <span className="label-accent">{s.role || "—"}</span>
                  <h4 className="font-display text-xl text-[color:var(--nccc-cream)]">
                    {s.name}
                  </h4>
                  <p className="label">{s.email}</p>
                </div>
                <div className="text-right">
                  <p className="label">{new Date(s.created_at).toUTCString().slice(5, 22)}</p>
                  <button
                    onClick={() => onDelete(s.id)}
                    className="text-[color:var(--nccc-muted)] hover:text-[color:var(--nccc-rust)] mt-2"
                    data-testid={`admin-submission-delete-${s.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {s.link && (
                <a
                  href={s.link}
                  target="_blank"
                  rel="noreferrer"
                  className="label-accent text-xs block mt-3"
                >
                  {s.link} ↗
                </a>
              )}
              <p className="mt-3 text-sm text-[color:var(--nccc-cream)] whitespace-pre-wrap leading-relaxed">
                {s.message}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
